import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import FormInput from "shared/FormInput";
import Button from "shared/Button";
import { ChevronDown, X } from "lucide-react";
import { Dialog } from "shared/Dialog/Dialog";
import { DialogContent } from "shared/Dialog/DialogContent";
import { DialogFooter } from "shared/Dialog/DialogFooter";
import { DialogHeader } from "shared/Dialog/DialogHeader";
import { DialogTitle } from "shared/Dialog/DialogTitle";
import * as yup from "yup";
import { iUser } from "core/kangarooTypes/iUser";
import { updateUser } from "core/Services/AdminServices";
import { toast } from "react-toastify";
import { Switch } from "shared/Switch";
import { useTranslation } from "react-i18next";

interface UserFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  phone_number: string;
  role: string;
  password?: string;
  active: boolean;
}

interface EditUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: iUser | null;
}

const EditUserDialog = ({
  isOpen,
  onClose,
  onSuccess,
  user,
}: EditUserDialogProps) => {
  const { t } = useTranslation();
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const phones = [
    { value: "+962", label: "+962 Jordan" },
    { value: "+966", label: "+966 Saudi Arabia" },
    { value: "+971", label: "+971 United Arab Emirates" },
    { value: "+965", label: "+965 Kuwait" },
    { value: "+973", label: "+973 Bahrain" },
    { value: "+974", label: "+974 Qatar" },
    { value: "+968", label: "+968 Oman" },
    { value: "+964", label: "+964 Iraq" },
    { value: "+963", label: "+963 Syria" },
    { value: "+961", label: "+961 Lebanon" },
    { value: "+970", label: "+970 Palestine" },
    { value: "+20", label: "+20 Egypt" },
    { value: "+90", label: "+90 Turkey" },
    { value: "+98", label: "+98 Iran" },
    { value: "+967", label: "+967 Yemen" },
    { value: "+249", label: "+249 Sudan" },
  ];
  const baseSchema = {
    first_name: yup
      .string()
      .required(t("errors.validation.firstNameRequired"))
      .trim(),
    last_name: yup
      .string()
      .required(t("errors.validation.lastNameRequired"))
      .trim(),
    email: yup
      .string()
      .email(t("errors.validation.invalidEmail"))
      .required(t("errors.validation.emailRequired"))
      .trim(),
    phone: yup
      .string()
      .required(t("errors.validation.phoneRequired"))
      .matches(/^\+\d+$/, t("errors.validation.invalidPhoneFormat")),
    phone_number: yup
      .string()
      .required(t("errors.validation.phoneNumberRequired"))
      .matches(/^\d+$/, t("errors.validation.phoneNumberDigitsOnly"))
      .transform((value) => (value === "" ? null : value)),
    role: yup.string().required(t("errors.validation.roleRequired")),
    active: yup.boolean().default(false),
  };

  const UpdateUserFormSchema = yup.object().shape({
    ...baseSchema,
    password: yup
      .string()
      .optional()
      .transform((value) => (value === "" ? undefined : value))
      .min(8, t("errors.validation.passwordMinLength")),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
    setValue,
  } = useForm<UserFormData>({
    resolver: yupResolver(UpdateUserFormSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "+962",
      phone_number: "",
      role: "",
      password: "",
      active: true,
    },
  });

  useEffect(() => {
    if (isOpen && user) {
      setIsActive(user.active);
      const phoneCode =
        phones.find((p) => user.phone_number.startsWith(p.value))?.value ||
        "+962";
      const phoneNumber = user.phone_number.replace(phoneCode, "");

      reset({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone: phoneCode,
        phone_number: phoneNumber,
        role: user.role,
        password: "",
        active: user.active,
      });
    }
  }, [isOpen, user, reset]);

  const roleOptions = [
    { value: "admin", label: "Admin" },
    { value: "operator", label: "Operator" },
    { value: "driver", label: "Driver" },
    { value: "accountant", label: "Accountant" },
  ];

  useEffect(() => {
    setValue("active", isActive);
  }, [isActive, setValue]);

  const handleFormSubmit = async (data: UserFormData) => {
    setIsSubmitting(true);

    try {
      await updateUser(
        {
          ...data,
          password: data.password ?? "",
          active: data.active,
          phone_number: `${data.phone}${data.phone_number}`,
        },
        user?.id || 0
      );
      toast.success(t("editUserDialog.userUpdatedSuccess"));
      onSuccess();
      handleClose();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || t("editUserDialog.updateUserFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    clearErrors();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex justify-between items-center">
            <span>{t("editUserDialog.title")}</span>
            <button
              onClick={handleClose}
              className="rounded-full p-1 hover:bg-gray-100 transition-colors"
            >
              <X size={18} />
            </button>
          </DialogTitle>
        </DialogHeader>

        <div className="h-full p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              name="first_name"
              label={t("editUserDialog.firstName")}
              placeholder={t("editUserDialog.enterFirstName")}
              register={register}
              errors={errors}
            />
            <FormInput
              name="last_name"
              label={t("editUserDialog.lastName")}
              placeholder={t("editUserDialog.enterLastName")}
              register={register}
              errors={errors}
            />

            <FormInput
              name="email"
              label={t("editUserDialog.email")}
              type="email"
              placeholder={t("editUserDialog.enterEmail")}
              register={register}
              errors={errors}
            />

            <div className="flex flex-col sm:flex-row gap-2">
              <div className="w-full sm:w-1/4">
                <FormInput
                  name="phone"
                  label={t("editUserDialog.phone")}
                  type="select"
                  register={register}
                  errors={errors}
                  selectOptions={phones}
                  icon={<ChevronDown size={20} />}
                />
              </div>
              <div className="w-full sm:w-3/4">
                <FormInput
                  label={t("editUserDialog.phoneNumber")}
                  name="phone_number"
                  type="text"
                  placeholder={t("editUserDialog.enterPhoneNumber")}
                  register={register}
                  errors={errors}
                />
              </div>
            </div>

            <FormInput
              name="role"
              label={t("editUserDialog.role")}
              type="select"
              placeholder={t("editUserDialog.selectRole")}
              icon={<ChevronDown size={20} />}
              register={register}
              errors={errors}
              selectOptions={roleOptions}
            />

            <FormInput
              name="password"
              label={t("editUserDialog.password")}
              type="password"
              placeholder={t("editUserDialog.enterNewPassword")}
              register={register}
              errors={errors}
            />

            <div className="flex items-center space-x-2">
              <label htmlFor="active-status" className="text-sm font-medium">
                {t("editUserDialog.activeStatus")}
              </label>
              <Switch
                id="active-status"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
              <span className="text-sm text-gray-500">
                {isActive
                  ? t("editUserDialog.active")
                  : t("editUserDialog.deactivated")}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outlined"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t("editUserDialog.cancel")}
          </Button>
          <Button
            onClick={handleSubmit(handleFormSubmit)}
            isLoading={isSubmitting}
          >
            {t("editUserDialog.updateUser")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserDialog;
